<template>
  <div class="space-y-4" v-if="!showConfirmation || pageMode">
    <template v-if="!pageMode">
      <div class="text-sm text-[#464646]">{{ t('propertyReport.form.description') }}</div>
      <hr class="mb-3 mt-3" />
    </template>

    <PropertyReportAddressStep
      ref="addressStepRef"
      :compact-map="pageMode"
      :show-buttons="!showExtended"
      @cancel="emitClose"
      @next="onAddressStepNext"
      @address-changed="onAddressChanged"
    >
      <template #extended v-if="showExtended">
        <div class="mt-4">
          <p class="text-xs text-[#171717] font-medium uppercase tracking-wide mb-4">{{
            t('propertyReport.form.primaryOwner') }}</p>
          <div class="flex flex-col gap-6">
            <div class="flex flex-col lg:flex-row gap-6">
              <div class="flex-1">
                <label class="block text-base text-[#000] mb-1">{{ t('propertyReport.form.firstName') }}</label>
                <input disabled v-model="ownerFirstName"
                  class="border border-[#D1D1D1] bg-[#F8F8F8] rounded p-2 w-full text-base text-[#6E6E6E] cursor-not-allowed" />
              </div>
              <div class="flex-1">
                <label class="block text-base text-[#000] mb-1">{{ t('propertyReport.form.lastName') }}</label>
                <input disabled v-model="ownerLastName"
                  class="border border-[#D1D1D1] bg-[#F8F8F8] rounded p-2 w-full text-base text-[#6E6E6E] cursor-not-allowed" />
              </div>
            </div>
            <div class="flex flex-col lg:flex-row gap-6">
              <div class="flex-1">
                <label class="block text-base text-[#000] mb-1">{{ t('propertyReport.form.phoneOptional') }} <span class="text-[#6E6E6E]">{{ t('propertyReport.form.optional') }}</span></label>
                <input v-model="ownerPhone" @input="validatePhone" @blur="formatPhone" maxlength="14"
                  class="border border-[#D1D1D1] bg-[#fff] rounded p-2 w-full text-base text-[#000]" />
                <p v-if="!validPhoneNumber" class="text-xs text-[#AF1E2C]">
                  {{ t('propertyReport.form.invalidPhoneNumber') }}
                </p>
              </div>
              <div class="flex-1">
                <label class="block text-base text-[#000] mb-1">{{ t('propertyReport.form.emailOptional') }} <span class="text-[#6E6E6E]">{{ t('propertyReport.form.optional') }}</span></label>
                <input v-model="ownerEmail" @blur="validateEmail" type="email"
                  class="border border-[#D1D1D1] bg-[#fff] rounded p-2 w-full text-base" />
                <p v-if="!validEmailAddress" class="text-xs text-[#AF1E2C]">{{
                  t('propertyReport.form.invalidEmailAddress')
                  }}
                </p>
              </div>
            </div>
            <hr class="m-0" />
            <div class="flex flex-col lg:flex-row gap-6">
              <div class="flex-1">
                <label class="block text-base text-[#000] mb-1">{{ t('propertyReport.form.salesRep') }} <span
                    class="text-[#C61E1F] italic">*</span></label>
                <div class="relative">
                  <select v-model="selectedRepId" :disabled="!userStore.canSelectOtherUser"
                    class="bg-[#fff] border border-[#D1D1D1] rounded p-2 text-base text-[#171717] appearance-none pr-8 focus:outline-none"
                    :class="!userStore.canSelectOtherUser ? 'cursor-not-allowed opacity-60' : ''">
                    <option v-for="rep in salesReps" :value="rep.Id" :key="rep.Id">{{ rep.FirstName }} {{ rep.LastName }}
                    </option>
                  </select>
                  <i
                    class="ri-arrow-down-s-line text-xl absolute right-0 mr-3 top-1/2 -translate-y-1/2 text-[#171717] pointer-events-none"></i>
                </div>
                <p v-if="salesRepsLoaded && !selectedSalesRep" class="text-xs text-[#AF1E2C] font-semibold">{{
                  t('propertyReport.form.salesRepRequired') }}</p>
              </div>
              <div class="flex-1 hidden lg:block"></div>
            </div>
          </div>
        </div>
      </template>
    </PropertyReportAddressStep>

    <!-- Payment Method Section (Feature Flag: rh-credit-card-property-reports) - Hidden when free trials remaining -->
    <PropertyReportPaymentMethodSelector v-if="showExtended && isCreditCardPropertyReportsEnabled && !hasFreeTrialsRemaining"
      v-model="selectedPaymentMethod" :show-credit-line="showCreditLineOption" :show-credit-card="showCreditCardOption"
      class="mt-4" />

    <!-- Actions AFTER extended shown -->
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 pt-2" v-if="showExtended">
      <p v-if="showCreditCardSurchargeNotice" class="text-xs text-[#6E6E6E] lg:max-w-[60%]">
        {{ t('propertyReport.creditCardSurchargeNotice') }}
      </p>
      <div class="flex justify-end gap-3 lg:ml-auto">
      <BaseButton variant="tertiary" size="custom" class="px-4 py-2 text-base" type="button" @click="emitClose">
        {{ t('propertyReport.form.cancel') }}
      </BaseButton>
      <BaseButton size="custom"
        v-if="!selectedSalesRep || !validPhoneNumber || !validEmailAddress || placingOrder || (isCreditCardPropertyReportsEnabled && !selectedPaymentMethod && !hasFreeTrialsRemaining)"
        disabled class="!rounded px-3 py-2 text-base">{{
        ctaButtonText
        }}</BaseButton>
      <BaseButton size="custom" v-else class="!rounded px-3 py-2 text-base"
        @click="handleContinueClick">{{ ctaButtonText }}</BaseButton>
      </div>
    </div>
    <p v-if="showExtended && userStore.hasPriceViewerPermission && pricePerReport >= 0 && hasCODTermsCode"
      class="text-sm text-[#6E6E6E] text-right mb-2">{{
        t('propertyReport.form.codOrderChargeNotice', { price: formatPrice(pricePerReport) }) }}</p>
    <p v-else-if="showExtended && userStore.hasPriceViewerPermission && pricePerReport >= 0"
      class="text-sm text-[#6E6E6E] text-right mb-2">{{
        t('propertyReport.form.orderChargeNotice', { price: pricePerReport, uom: uomReport }) }}</p>
    <p v-if="showExtended && !userStore.hasPriceViewerPermission" class="text-sm text-[#6E6E6E] text-right mb-2">{{
      t('propertyReport.form.priceWillBeAssignedAtBranch') }}</p>
    <p v-if="showExtended && userStore.hasPriceViewerPermission && pricePerReport < 0"
      class="text-sm text-[#6E6E6E] text-right mb-2">{{
        t('propertyReport.form.pricingUnavailableNotice') }}</p>
  </div>

  <!-- Credit Card Payment Modal -->
  <PropertyReportCreditCardModal :show="showCreditCardModal" :iframe-url="worldPayIframeUrl"
    :is-loading="isLoadingWorldPay" :error-message="worldPayErrorMessage" :is-placing-order="placingOrder"
    :is-validating="isValidatingPayment" :payment-declined="paymentDeclined"
    :payment-declined-message="paymentDeclinedMessage"
    @close="handleCreditCardModalClose" @place-order="handleCreditCardPlaceOrder"
    @payment-submitted="handlePaymentSubmitted" />

  <!-- Success Modal for pageMode -->
  <PropertyReportSuccessModal v-if="pageMode" :show="showConfirmation" :transaction-id="transactionId"
    @done="onSuccessModalDone" @close="onSuccessModalClose" />

  <!-- Success Confirmation - Inline for modal usage -->
  <div v-if="showConfirmation && !pageMode" class="space-y-6 text-center py-8">
    <div class="flex justify-center">
      <div class="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
        <svg width="51" height="50" viewBox="0 0 51 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M25.5 50C11.6929 50 0.5 38.807 0.5 25C0.5 11.1929 11.6929 0 25.5 0C39.307 0 50.5 11.1929 50.5 25C50.5 38.807 39.307 50 25.5 50ZM23.0065 35L40.6842 17.3223L37.1488 13.7868L23.0065 27.929L15.9355 20.8577L12.4 24.3935L23.0065 35Z"
            fill="#248726">
          </path>
        </svg>
      </div>
    </div>

    <!-- Success Message -->
    <div class="space-y-4">
      <h2 class="text-2xl font-semibold text-[#171717]">{{ t('propertyReport.success.title') }}</h2>
      <div class="space-y-2 text-sm text-[#464646]">
        <p>{{ t('propertyReport.success.description1') }}</p>
        <p>{{ t('propertyReport.success.description2') }}</p>
      </div>
      <div class="pt-2">
        <p class="text-base font-semibold text-[#464646]">{{ t('propertyReport.success.confirmationNumber', {
          id:
          transactionId }) }}</p>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
      <button @click="copyConfirmationNumber"
        class="flex items-center gap-2 px-4 py-2 text-sm font-normal text-[#171717] border border-[#D1D1D1] rounded hover:bg-gray-50 transition-colors">
        <i class="ri-file-copy-line text-base"></i>
        {{ t('propertyReport.success.copyConfirmationNumber') }}
      </button>
      <BaseButton size="custom" class="!rounded px-6 py-2 text-sm !font-medium"
        @click="navigateToPropertyReports">
        {{ t('propertyReport.success.done') }}
      </BaseButton>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PropertyReportAddressStep from './PropertyReportAddressStep.vue'
import PropertyReportSuccessModal from './PropertyReportSuccessModal.vue'
import { PropertyReportPaymentMethodSelector, PaymentMethodType, PropertyReportCreditCardModal } from '@/components/PropertyReportPayment'
import { useUserStore } from '@/stores/userStore'
import { usePropertyReportFormStore } from '@/stores/propertyReportFormStore'
import accountService from '@/services/accountService'
import propertyReportsService from '@/services/propertyReportsService'
import WorldPayService from '@/services/worldPayService'
import { getToken, getLanguage } from '@/utilities/cookies';
import BaseButton from '@/components/atoms/buttons/BaseButton.vue';

const { t, locale } = useI18n()
locale.value = getLanguage();

const emit = defineEmits(['close', 'success', 'confirmationChanged'])
const props = defineProps({
  redirectToPropertyReports: { type: Boolean, default: false },
  pageMode: { type: Boolean, default: false }
})

const pageMode = computed(() => props.pageMode)

// COD terms codes - customers with these terms codes are COD customers
const COD_TERMS_CODES = ['cod', 'dcod', '2%cod', '2.5%cod', '3%cod', '5%cod', 'fcod', 'rcod']
const isCreditCardPropertyReportsEnabled = computed(() => window.appConfig?.enableCreditCardPropertyReporty === true)
const selectedPaymentMethod = ref(null)

// Credit Card Modal state
const showCreditCardModal = ref(false)
const worldPayIframeUrl = ref('')
const isLoadingWorldPay = ref(false)
const worldPayErrorMessage = ref('')
const worldPayToken = ref(null)

// Credit Card payment response evaluation state
const isValidatingPayment = ref(false)
const paymentDeclined = ref(false)
const paymentDeclinedMessage = ref('')
const creditCardPaymentAccountId = ref(null)

const addressStepRef = ref(null)
const userStore = useUserStore()
const formStore = usePropertyReportFormStore()
const hasFreeTrialsRemaining = computed(() => formStore.freeReportsRemaining > 0)
const addressObj = ref({})
const selectedBranchCode = ref(null)
const selectedShipToSequence = ref(null)
const selectedJobAccount = ref(null)
const pricePerReport = ref(-2)
const uomReport = ref('')
const currentUser = ref(null)

// Determine if selected job account is COD or non-COD based on TermsCode
const selectedJobAccountTermsCodes = computed(() => {
  const termsCode = selectedJobAccount.value?.TermsCode?.toLowerCase()?.trim() || ''
  return termsCode.split(/[\s,]+/).filter(code => code.length > 0)
})

const hasCODTermsCode = computed(() => {
  return selectedJobAccountTermsCodes.value.some(code => COD_TERMS_CODES.includes(code))
})

const showCreditLineOption = computed(() => !hasCODTermsCode.value)

const showCreditCardOption = computed(() => hasCODTermsCode.value)

const showCreditCardSurchargeNotice = computed(() =>
  isCreditCardPropertyReportsEnabled.value && !hasFreeTrialsRemaining.value && hasCODTermsCode.value
)

const ownerData = ref(null)
const ownerFirstName = ref('')
const ownerLastName = ref('')
const ownerPhone = ref('')
const ownerEmail = ref('')
const showExtended = ref(false)

function onAddressChanged() {
  showExtended.value = false
  selectedPaymentMethod.value = null
}

function onAddressStepNext(data) {
  addressObj.value = data.addressObj
  ownerData.value = data.ownerData
  selectedBranchCode.value = data.selectedBranchCode
  selectedShipToSequence.value = data.selectedShipToSequence
  selectedJobAccount.value = data.selectedJobAccount
  pricePerReport.value = data.pricePerReport
  uomReport.value = data.uomReport
  currentUser.value = data.currentUser

  if (ownerData.value) {
    ownerFirstName.value = ownerData.value.PrimaryOwnerFirstName || ''
    ownerLastName.value = ownerData.value.PrimaryOwnerLastName || ''
    ownerEmail.value = ownerData.value.PrimaryOwnerEmailAddress || ''
    ownerPhone.value = ownerData.value.PrimaryOwnerPhoneNumber || ''
  } else {
    ownerFirstName.value = ownerLastName.value = ownerEmail.value = ownerPhone.value = ''
  }

  showExtended.value = true
}

function rawPhone(v) { return (v || '').replace(/\D/g, '').slice(0, 10) }
function validatePhone(e) { ownerPhone.value = rawPhone(e.target.value) }
function formatPhone() { const d = rawPhone(ownerPhone.value); if (d.length === 10) ownerPhone.value = `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` }
const validPhoneNumber = computed(() => { const d = rawPhone(ownerPhone.value); return d.length === 10 || d.length === 0 })

function validateEmail() { }
const validEmailAddress = computed(() => {
  if (!ownerEmail.value || ownerEmail.value.trim() === '') return true
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(ownerEmail.value.trim())
})

// Sales reps (users)
const salesReps = ref([])
const selectedRepId = ref(null)
const salesRepsLoaded = ref(false)
async function loadUsers() {
  const res = await accountService.fetchUserDetails(getToken())
  if (res.success) {
    const user = res.data
    currentUser.value = user
    // If user can select other users (Owner/Executive or Back Office), fetch all users
    if (userStore.canSelectOtherUser) {
      const usersRes = await accountService.getUsersForCompany({ IsActive: true })
      if (usersRes.success && Array.isArray(usersRes.data)) {
        salesReps.value = usersRes.data.sort((a, b) => {
          const nameA = `${a.FirstName} ${a.LastName}`.toLowerCase()
          const nameB = `${b.FirstName} ${b.LastName}`.toLowerCase()
          return nameA.localeCompare(nameB)
        })
      } else {
        salesReps.value = [user]
      }
    } else {
      // For other roles, only show current user
      salesReps.value = [user]
    }
    selectedRepId.value = user.Id
    salesRepsLoaded.value = true
  }
}
const selectedSalesRep = computed(() => salesReps.value.find(r => r.Id === selectedRepId.value))

/**
 * CTA button text based on payment method selection:
 * - Free trials remaining: "Place Order" (no payment needed)
 * - Credit Line selected (or only option): "Place Order"
 * - Credit Card selected (or only option): "Continue"
 */
const ctaButtonText = computed(() => {
  if (!isCreditCardPropertyReportsEnabled.value || hasFreeTrialsRemaining.value) {
    return t('propertyReport.form.placeOrder')
  }
  if (selectedPaymentMethod.value === PaymentMethodType.CreditLine) {
    return t('propertyReport.form.placeOrder')
  }
  return t('propertyReport.form.continue', 'Continue')
})

// Order placement
const placingOrder = ref(false);
const transactionId = ref(null);
const showConfirmation = ref(false);

// Watch showConfirmation and emit to parent
watch(showConfirmation, (newValue) => {
  emit('confirmationChanged', newValue)
})
async function placeOrder() {
  placingOrder.value = true
  const salesRep = selectedSalesRep.value
  // Get user details for customer contact address
  const user = currentUser.value
  const payload = {
    StreetNumber: addressObj.value.streetNumber,
    StreetAddress: addressObj.value.streetAddress,
    City: addressObj.value.city,
    State: addressObj.value.state,
    Zip: addressObj.value.zipCode,
    PrimaryOwnerPhoneNumber: rawPhone(ownerPhone.value),
    PrimaryOwnerEmailAddress: ownerEmail.value,
    IsRoofing: true,
    Locale: 'en',
    SalesRepresentativeFullName: salesRep ? `${salesRep.FirstName} ${salesRep.LastName}` : '',
    SalesRepresentativeEmail: salesRep?.Email || '',
    SalesRepresentativePhoneNumber: rawPhone(salesRep?.PhoneNumber),
    SalesRepresentativeOfficeNumber: rawPhone(salesRep?.PhoneNumber),
    OrderSubmission: {
      SourceSystem: 'ROOFHUB',
      BranchId: selectedBranchCode.value,
      ShipToSequenceNumber: selectedShipToSequence.value || 1,
      PaymentAccountId: null,
      CustomerContactInfo: {
        CustomerContactEmail: salesRep?.Email || user?.Email,
        CustomerContactPhone: salesRep?.PhoneNumber || user?.CustomerDetail?.Phone,
        CustomerContactName: salesRep ? `${salesRep.FirstName} ${salesRep.LastName}` : user?.CustomerDetail?.CustomerName || '',
        CustomerContactAddress: {
          AddressLine1: user?.CustomerDetail?.AddressLine1 || '',
          AddressLine2: user?.CustomerDetail?.AddressLine2 || '',
          AddressLine3: user?.CustomerDetail?.AddressLine3 || '',
          City: user?.CustomerDetail?.City || '',
          State: user?.CustomerDetail?.State || '',
          ZipCode: user?.CustomerDetail?.ZipCode || ''
        },
        AdditionalContactEmails: []
      }
    }
  }
  
  if (selectedPaymentMethod.value === PaymentMethodType.CreditCard) {
    payload.OrderSubmission.HeaderNote = t('propertyReport.creditCardHeaderNote');
    payload.OrderSubmission.HeaderNoteHot = true;
  }

  // Add PaymentAccountId only for credit card payments
  if (selectedPaymentMethod.value === PaymentMethodType.CreditCard && creditCardPaymentAccountId.value) {
    payload.OrderSubmission.PaymentAccountId = creditCardPaymentAccountId.value;
  }

  const resp = await propertyReportsService.create(payload)
  placingOrder.value = false
  if (resp.success && resp.data.reportId) {
    transactionId.value = resp.data.reportId;
    showConfirmation.value = true;
    showCreditCardModal.value = false;

    // For credit card payments, associate the created report with its PaymentTransaction
    // by storing the reportId into the OrderId column (matched by the WorldPay token).
    // Best-effort: never disrupt the confirmed report if this update fails.
    if (selectedPaymentMethod.value === PaymentMethodType.CreditCard && worldPayToken.value) {
      try {
        const updateResp = await WorldPayService.updateOrderId(worldPayToken.value, transactionId.value);

        if (!updateResp.success) {
          console.error('Failed to update PaymentTransaction OrderId:', updateResp.error)
        }
      } catch (error) {
        console.error('Error updating PaymentTransaction OrderId:', error)
      }
    }
  }
  else {
    if (typeof window.toastr !== 'undefined') {
      window.toastr.error(t('propertyReport.form.orderFailedError'));
    }
    emitClose();
  }
}

function handleContinueClick() {
  if (hasFreeTrialsRemaining.value) {
    placeOrder()
    return
  }
  
  if (selectedPaymentMethod.value === PaymentMethodType.CreditCard) {
    openCreditCardModal()
  } else {
    placeOrder()
  }
}


async function openCreditCardModal() {
  showCreditCardModal.value = true
  isLoadingWorldPay.value = true
  worldPayIframeUrl.value = ''
  worldPayErrorMessage.value = ''
  isValidatingPayment.value = false
  paymentDeclined.value = false
  paymentDeclinedMessage.value = ''
  creditCardPaymentAccountId.value = null

  try {
    const salesRep = selectedSalesRep.value
    const user = currentUser.value

    const paymentDetails = {
      customerCode: user?.CustomerDetail?.CustomerCode || '',
      amount: pricePerReport.value >= 0 ? Number(formatPrice(pricePerReport.value)) : 0,
      orderId: '',
      currency: 'USD',
      billingName: user?.CustomerDetail?.CustomerName || '',
      billingEmail: user?.Email || '',
      billingPhone: user?.CustomerDetail?.Phone || '',
      billingAddress1: user?.CustomerDetail?.AddressLine1 || '',
      billingAddress2: user?.CustomerDetail?.AddressLine2 || '',
      billingCity: user?.CustomerDetail?.City || '',
      billingState: user?.CustomerDetail?.State || '',
      billingZipcode: user?.CustomerDetail?.ZipCode || ''
    }

    const response = await WorldPayService.generateToken(paymentDetails)

    if (response.success && response.data?.iframeUrl) {
      worldPayIframeUrl.value = response.data.iframeUrl
      worldPayToken.value = response.data.token || null
    } else {
      worldPayErrorMessage.value = response.error || 'Failed to load payment form'
    }
  } catch (error) {
    console.error('Error loading WorldPay iframe:', error)
    worldPayErrorMessage.value = 'Failed to load payment form. Please try again.'
  } finally {
    isLoadingWorldPay.value = false
  }
}

function handleCreditCardModalClose() {
  showCreditCardModal.value = false
  worldPayIframeUrl.value = ''
  worldPayErrorMessage.value = ''
  worldPayToken.value = null
  isValidatingPayment.value = false
  paymentDeclined.value = false
  paymentDeclinedMessage.value = ''
  creditCardPaymentAccountId.value = null
}

/**
 * Called by the credit card modal once the WorldPay payment form has been submitted.
 * Retrieves the payment response and either:
 *  - blocks report creation (generic error + disabled action) when there is no
 *    PaymentAccountId or the ExpressResponseMessage is not 'Approved', or
 *  - closes the iframe and creates the report automatically when the payment is approved.
 */
async function handlePaymentSubmitted() {
  paymentDeclined.value = false
  paymentDeclinedMessage.value = ''
  creditCardPaymentAccountId.value = null
  isValidatingPayment.value = true

  try {
    const resp = await WorldPayService.getPaymentAccountId(worldPayToken.value)
    const paymentInfo = resp.success ? resp.data : null

    const isApproved =
      !!paymentInfo &&
      !!paymentInfo.paymentAccountId &&
      (paymentInfo.expressResponseMessage == null ||
        paymentInfo.expressResponseMessage === 'Approved')

    if (!isApproved) {
      paymentDeclined.value = true
      paymentDeclinedMessage.value = t('propertyReport.creditCardPaymentDeclined')
      return
    }

    creditCardPaymentAccountId.value = paymentInfo.paymentAccountId
    showCreditCardModal.value = false
    await placeOrder()
  } catch (error) {
    console.error('Error evaluating WorldPay payment response:', error)
    paymentDeclined.value = true
    paymentDeclinedMessage.value = t('propertyReport.creditCardPaymentDeclined')
  } finally {
    isValidatingPayment.value = false
  }
}

function handleCreditCardPlaceOrder() {
  placeOrder()
}

function emitClose() { emit('close') }

function navigateToPropertyReports() {
  emit('success', { reportId: transactionId.value });
  if (props.redirectToPropertyReports) {
    window.location.href = '/property-reports';
  } else {
    // Close popup if from estimator build proposal step
    emitClose();
  }
}

function onSuccessModalDone() {
  emit('success', { reportId: transactionId.value })
}

function onSuccessModalClose() {
  // Close modal and reset form without navigating
  showConfirmation.value = false
  transactionId.value = null
  showExtended.value = false

  // Reset owner fields
  ownerFirstName.value = ''
  ownerLastName.value = ''
  ownerPhone.value = ''
  ownerEmail.value = ''
  ownerData.value = null
  addressObj.value = {
    streetNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: ''
  }
  
  selectedBranchCode.value = null
  selectedShipToSequence.value = null
  selectedJobAccount.value = null
  pricePerReport.value = -2
  uomReport.value = ''
  selectedPaymentMethod.value = null

  if (addressStepRef.value?.clearAddress) {
    addressStepRef.value.clearAddress()
  }
}

function copyConfirmationNumber() {
  if (transactionId.value) {
    navigator.clipboard.writeText(transactionId.value).then(() => {
      if (typeof window.toastr !== 'undefined') {
        window.toastr.success(t('propertyReport.success.confirmationCopied'));
      }
    }).catch(() => {
      if (typeof window.toastr !== 'undefined') {
        window.toastr.error('Failed to copy confirmation number');
      }
    });
  }
}

function formatPrice(price) {
  if (price === null || price === undefined) return '0.00';
  return Number(price).toFixed(2);
}

const isInitializing = ref(true);

onMounted(async () => {
  try {
    await loadUsers()
  } catch (error) {
    console.error('Error initializing form:', error)
  } finally {
    isInitializing.value = false
  }
})
</script>
